import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { searchFolders, searchDocuments } from '../services/closeoutDocsService';

/**
 * CloseoutDocsSearch Component
 *
 * Provides real-time search functionality for documents and folders.
 * Features:
 * - Debounced search (700ms)
 * - Query validation (2-100 characters)
 * - Top 100 results with warning
 * - Separate results for folders and documents
 *
 * @param {string} projectId - The UUID of the project to search in
 * @param {function} onDocumentClick - Callback when a document is clicked (doc) => void
 * @param {function} onFolderClick - Callback when a folder is clicked (folder) => void
 * @param {function} onError - Callback for error handling (message, severity) => void
 */
const CloseoutDocsSearch = ({ projectId, onDocumentClick, onFolderClick, onError }) => {
    const theme = useTheme();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState({ folders: [], documents: [] });
    const [isSearching, setIsSearching] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchResultsLimited, setSearchResultsLimited] = useState(false);
    const searchInputRef = useRef(null);

    /**
     * Debounced search effect
     * Searches folders and documents after user stops typing for 700ms
     * Validates query length (2-100 characters)
     */
    useEffect(() => {
        const trimmedQuery = searchQuery.trim();

        // Don't search for empty or very short queries
        if (trimmedQuery.length < 2) {
            setSearchResults({ folders: [], documents: [] });
            setIsSearching(false);
            setSearchOpen(false);
            setSearchResultsLimited(false);
            return;
        }

        // Validate max query length (frontend validation)
        if (trimmedQuery.length > 100) {
            onError('Search query cannot exceed 100 characters', 'warning');
            return;
        }

        // Don't open dropdown or show any state yet - just wait for debounce
        setSearchOpen(false);
        setIsSearching(false);

        // Create an AbortController to cancel the request if needed
        const abortController = new AbortController();

        // Debounce the search - only execute after user stops typing for 0.7 second
        const timeoutId = setTimeout(async () => {
            // User has stopped typing - now show loading and open dropdown
            if (!abortController.signal.aborted) {
                setSearchOpen(true);
                setIsSearching(true);
            }

            try {
                // Search both folders and documents in parallel
                const [foldersResponse, documentsResponse] = await Promise.all([
                    searchFolders(projectId, trimmedQuery),
                    searchDocuments(projectId, trimmedQuery),
                ]);

                // Only update state if this request wasn't aborted
                if (!abortController.signal.aborted) {
                    setSearchResults({
                        folders: foldersResponse.data,
                        documents: documentsResponse.data,
                    });
                    // Show warning if either result set was limited
                    setSearchResultsLimited(
                        foldersResponse.resultsLimited || documentsResponse.resultsLimited
                    );
                    setIsSearching(false);
                }
            } catch (err) {
                // Only show error if request wasn't aborted
                if (!abortController.signal.aborted) {
                    console.error('Search failed:', err);
                    setSearchResults({ folders: [], documents: [] });
                    setSearchResultsLimited(false);
                    setIsSearching(false);
                    if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
                        onError(err.message || 'Search failed. Please try again.', 'error');
                    }
                }
            }
        }, 700);

        // Cleanup: cancel timeout and abort any in-flight request
        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [searchQuery, projectId, onError]);

    /**
     * Handle search input change
     */
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    /**
     * Handle search clear
     */
    const handleSearchClear = () => {
        setSearchQuery('');
        setSearchResults({ folders: [], documents: [] });
        setSearchOpen(false);
        setIsSearching(false);
        setSearchResultsLimited(false);
    };

    /**
     * Handle search popover close
     */
    const handleSearchClose = () => {
        setSearchOpen(false);
    };

    /**
     * Handle folder search result click
     */
    const handleFolderClick = (folder) => {
        onFolderClick(folder);
        handleSearchClear();
    };

    /**
     * Handle document search result click
     */
    const handleDocumentClick = (document) => {
        onDocumentClick(document);
        handleSearchClear();
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <TextField
                fullWidth
                placeholder="Search documents and folders..."
                value={searchQuery}
                onChange={handleSearchChange}
                inputRef={searchInputRef}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: theme.palette.grey[500] }} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                {isSearching ? (
                                    <CircularProgress size={20} />
                                ) : searchQuery ? (
                                    <IconButton size="small" onClick={handleSearchClear} edge="end">
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                ) : null}
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: theme.palette.primary.main,
                        },
                    },
                }}
            />

            {/* Search Results Popover */}
            <Popover
                open={searchOpen && searchQuery.trim().length >= 2}
                anchorEl={searchInputRef.current}
                onClose={handleSearchClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: searchInputRef.current?.offsetWidth || 400,
                            maxHeight: 400,
                            mt: 1,
                            overflow: 'auto',
                            boxShadow: theme.shadows[8],
                            borderRadius: 2,
                        },
                    },
                }}
            >
                {isSearching ? (
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Searching...
                        </Typography>
                    </Box>
                ) : searchResults.folders.length > 0 || searchResults.documents.length > 0 ? (
                    <List sx={{ p: 0 }}>
                        {/* Results Limited Warning */}
                        {searchResultsLimited && (
                            <Box
                                sx={{
                                    p: 1.5,
                                    backgroundColor: theme.palette.warning.light,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: theme.palette.warning.dark,
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    ⚠️ Showing top 100 results. Refine your search for more specific
                                    results.
                                </Typography>
                            </Box>
                        )}

                        {/* Folders First */}
                        {searchResults.folders.map((folder) => (
                            <ListItem key={`folder-${folder.folderId}`} disablePadding>
                                <ListItemButton
                                    onClick={() => handleFolderClick(folder)}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <FolderIcon
                                            sx={{
                                                color: theme.palette.warning.main,
                                                fontSize: 24,
                                            }}
                                        />
                                    </ListItemIcon>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {folder.folderName}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Folder
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        ))}

                        {/* Documents */}
                        {searchResults.documents.map((doc) => (
                            <ListItem key={`doc-${doc.documentId}`} disablePadding>
                                <ListItemButton
                                    onClick={() => handleDocumentClick(doc)}
                                    sx={{
                                        py: 1.5,
                                        px: 2,
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <InsertDriveFileIcon
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontSize: 24,
                                            }}
                                        />
                                    </ListItemIcon>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minWidth: 0,
                                            flex: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {doc.originalFilename}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {doc.fileExtension?.toUpperCase()}
                                            {doc.fileSizeBytes &&
                                                ` • ${(doc.fileSizeBytes / 1024).toFixed(0)} KB`}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No results found
                        </Typography>
                    </Box>
                )}
            </Popover>
        </Box>
    );
};

export default CloseoutDocsSearch;
